import { Redirect, Route, Switch } from "react-router-dom";
import { Suspense, lazy, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import AlertBox from "components/global/AlertBox";
import Loader from "components/global/Loader";
import Navbar from "components/global/Navbar";
import Onboarding from "views/Onboarding";
import ScrollToTop from "components/global/ScrollToTop";
import Spacer from "components/global/Spacer";
import SwitchUser from "views/SwitchUser";
import { connect } from "react-redux";
import styled from "styled-components";
import { useEffect } from "react";
import NewSidebar from "components/global/sidebar/Sidebar";
import { getTooltip, getUserAccounts } from "api";
import { useHistory } from "react-router-dom";
import { ChannelsProvider } from "ws/Channels";
import AiContextProvider from "contexts/AiContext";
import WelcomeBack from "components/global/WelcomeBack";
import { lazyRetry } from "components/global/LazyRetry";
import PaymentFailureModal from "views/plans/PaymentFailureModal";
import CustomErrorHandler from "components/global/ErrorBoundary";
import HomeDashboard from "views/home/HomeDashboard";
import NewFeatureEngagement from "components/global/engagementModals/NewFeatureEngagement";
import UserLockedModal from "components/global/UserLockedModal";
import TutorialContextProvider from "contexts/TutorialContext";
import NewMediaLibraryContextProvider from "contexts/MediaLibraryContext";
import PostContextProvider from "contexts/PostContext";
import { ModalProvider } from "contexts/ModalContext";
import { showAlert } from "utils/utils";
import QueueConfig from "views/postPlanner/queue/QueueConfig";
import WebPromoBox from "./WebPromoBox";
import WelcomeSearchPrompt from "./WelcomeSearchPrompt";
import AppWideVideoTutorial from "./AppWideVideoTutorial";
import VerifyEmailBanner from "./VerifyEmailBanner";
import VerifyEmailPrompt from "./VerifyEmailPrompt";

const Brands = lazy(() =>
  lazyRetry(() => import("views/brands/Brands"), "brands")
);
const NaraFeature = lazy(() =>
  lazyRetry(() => import("views/naraNew/NaraIndex"), "nara")
);
const TeamMembers = lazy(() =>
  lazyRetry(() => import("views/teamMembers/TeamMembers"), "teamMember")
);
const UserAccount = lazy(() =>
  lazyRetry(() => import("views/userAccount/UserAccount"), "userAccount")
);
const IgGridPlanner = lazy(() =>
  lazyRetry(() => import("views/igGridPlanner/IgGridPlanner"), "igGridPlanner")
);
const PostPlanner = lazy(() =>
  lazyRetry(() => import("views/postPlanner/PlannerIndex"), "postPlanner")
);
const Plans = lazy(() =>
  lazyRetry(() => import("views/plans/PlansNew"), "plans")
);
const WorkspaceSetting = lazy(() =>
  lazyRetry(() => import("views/workspace/WorkspaceSettings"), "workspace")
);
const Billing = lazy(() =>
  lazyRetry(() => import("views/billing/Billing"), "billing")
);
const DirectMessages = lazy(() =>
  lazyRetry(
    () => import("views/directMessage/DirectMessageLayout"),
    "directMessage"
  )
);
const CommentLayout = lazy(() =>
  lazyRetry(() => import("views/comments/CommentLayout"), "aiAssistant")
);
const AiAssistant = lazy(() => import("views/aiAssistant/AiAssistantRoutes"));
const BulkSchedule = lazy(() => import("views/bulkSchedule"));
const Affiliates = lazy(() =>
  lazyRetry(
    () => import("views/affiliates/AffiliatesPage"),
    "affiliate-program"
  )
);
const Automation = lazy(() =>
  lazyRetry(() => import("views/automation/Automation"), "automation")
);
const Crm = lazy(() => lazyRetry(() => import("views/crm/Crm"), "crm"));
const GlobalMediaLibrary = lazy(() =>
  lazyRetry(
    () => import("views/mediaLibraryNew/GlobalMediaLibrary"),
    "mediaLib"
  )
);
const Analytics = lazy(() =>
  lazyRetry(() => import("views/analytics/Analytics"), "analytics")
);

const Wrapper = styled.div`
  transition: all 0.2s ease-out;

  .dashboardContent {
    position: relative;
    margin-left: 240px;
    margin-top: 7.2rem;
    min-height: calc(100vh - 7.2rem);

    > .alertWrapper {
      position: fixed;
      left: 24rem;
      top: 7.2rem;
      width: calc(100vw - 24rem);
      z-index: 10;
    }
  }

  @media screen and (max-width: 768px) {
    .dashboardContent {
      margin-left: 0;
      margin-top: 14.4rem;
      min-height: calc(100vh - 14.4rem);
    }
  }
`;

const LoadingWrapper = styled.div`
  height: 100vh;
  width: 100%;

  img.gif {
    height: 100px;
  }
`;

const Dashboard = ({
  logged_in,
  user,
  user_token,
  active_account,
  onboarding_state,
  member,
  show_app_modal,
}) => {
  const history = useHistory();
  const [hideOnboarding, setHideOnboarding] = useState(true);
  const [loading, setLoading] = useState(true);
  const isAccountOwner = member?.is_owner_of_account === 1;

  const fetchUserAccounts = async () => {
    !loading && setLoading(true);
    try {
      const res = await getUserAccounts();

      if (res?.data?.status === true) {
        await getTooltip();
        if (window.location.pathname.includes("accept-invite")) {
          setLoading(false);
          return;
        }
        if (
          (!res.data?.data?.accounts.length &&
            !window.location.pathname.includes("accept-invite")) ||
          (!onboarding_state.onboarding_completed && isAccountOwner)
        ) {
          history.push("/onboarding");
          return;
        } else {
          setLoading(false);
          return;
        }
      }

      setLoading(false);
    } catch (e) {
      showAlert({
        msg: "Failed to fetch user accounts. Try reloading the page",
        type: "danger",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAccounts();

    // Clear redirect path storage
    localStorage.removeItem("redirect");
    sessionStorage.removeItem("return_to");
    // eslint-disable-next-line
  }, [active_account?.id]);

  if (!logged_in) {
    return (
      <Redirect
        to={`/sign-in?return_to=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`}
      />
    );
  }

  if (loading) {
    return (
      <LoadingWrapper className="flexColumn alignCenter justifyCenter">
        <Loader />
      </LoadingWrapper>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={CustomErrorHandler}>
      <ChannelsProvider authToken={user_token} authUser={user}>
        <PostContextProvider>
          <AiContextProvider>
            <TutorialContextProvider>
              <NewMediaLibraryContextProvider>
                <ModalProvider>
                  <Wrapper>
                    <Onboarding
                      hide={hideOnboarding}
                      setHide={setHideOnboarding}
                    />
                    <AlertBox />
                    <NewSidebar />
                    <Navbar />
                    <div className="dashboardContent">
                      <ScrollToTop />
                      <SwitchUser />
                      <WelcomeBack />
                      {/* <WorkspaceTour /> */}
                      <NewFeatureEngagement />
                      <PaymentFailureModal />
                      <UserLockedModal />
                      <GlobalMediaLibrary />
                      {/* {device === "android" && showMobileAppBanner && (
                      <MobileAppBanner
                        setShowMobileAppBanner={setShowMobileAppBanner}
                      />
                    )}
                    {device === "android" && show_app_modal && (
                      <MobileAppModal />
                    )} */}
                      <WebPromoBox />
                      <WelcomeSearchPrompt />
                      <AppWideVideoTutorial />
                      <VerifyEmailBanner />
                      <VerifyEmailPrompt />
                      <Suspense
                        fallback={
                          <div>
                            <Spacer y={9.6} />
                            <Loader />
                          </div>
                        }
                      >
                        <Switch>
                          <Route
                            exact
                            path="/dashboard"
                            component={() => <Redirect to="/dashboard/home" />}
                          />
                          <Route
                            path="/dashboard/nara"
                            component={NaraFeature}
                          />
                          <Route
                            path="/dashboard/home"
                            component={HomeDashboard}
                          />
                          <Route
                            path="/dashboard/posts"
                            component={PostPlanner}
                          />
                          <Route
                            path="/dashboard/queue/queue-settings"
                            component={QueueConfig}
                          />
                          <Route
                            path="/dashboard/bulk-schedule"
                            component={BulkSchedule}
                          />
                          <Route path="/dashboard/brands" component={Brands} />
                          <Route
                            path="/dashboard/team-members"
                            component={TeamMembers}
                          />
                          <Route
                            path="/dashboard/profile"
                            component={UserAccount}
                          />
                          <Route
                            path="/dashboard/ig-grid-planner"
                            component={IgGridPlanner}
                          />
                          <Route path="/dashboard/plans" component={Plans} />
                          <Route
                            path="/dashboard/workspace-settings"
                            component={WorkspaceSetting}
                          />
                          <Route
                            path="/dashboard/billing"
                            component={Billing}
                          />
                          <Route
                            path="/dashboard/direct-messages"
                            component={DirectMessages}
                          />
                          <Route
                            path="/dashboard/comments"
                            component={CommentLayout}
                          />
                          <Route
                            path="/dashboard/ai-assistant"
                            component={AiAssistant}
                          />
                          <Route
                            path="/dashboard/affiliate-program"
                            component={Affiliates}
                          />
                          <Route
                            path="/dashboard/analytics"
                            component={Analytics}
                          />
                          <Route
                            path="/dashboard/automation"
                            component={Automation}
                          />
                          <Route path="/dashboard/crm" component={Crm} />
                          {/* No match */}
                          <Route
                            component={() => <Redirect to="/dashboard/home" />}
                          />
                        </Switch>
                      </Suspense>
                    </div>
                  </Wrapper>
                </ModalProvider>
              </NewMediaLibraryContextProvider>
            </TutorialContextProvider>
          </AiContextProvider>
        </PostContextProvider>
      </ChannelsProvider>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state) => {
  return {
    logged_in: state.logged_in,
    user: state.user,
    active_account: state.active_account,
    user_token: state.user_token,
    current_sub: state.current_sub,
    onboarding_state: state.guided_tours,
    member: state.member,
    show_app_modal: state.show_app_modal,
  };
};

export default connect(mapStateToProps)(Dashboard);
